import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AccountInfo, AuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { setOsduTokenProvider, setGraphTokenProvider } from '../api/client';
import { getCurrentEnvironment, getEnvironmentConfig } from '../config/environments';
import { msalInstance, loginRequest, getOsduTokenRequest, graphTokenRequest } from '../config/authConfig';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  osduToken: string | null;
  user: AccountInfo | null;
  dataPartitionId: string;
  baseURL: string;
  login: () => Promise<void>;
  logout: () => void;
  setDataPartitionId: (id: string) => void;
  setBaseURL: (url: string) => void;
  updateToken: (token: string) => void;
  refreshOsduToken: () => Promise<void>;
  acquireGraphToken: () => Promise<string | null>;
  isLoading: boolean;
  /** Human-readable description of what auth is currently doing. */
  loadingMessage: string;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// MSAL browser error codes that mean "silent path can't complete — the user
// needs to be interactively re-authenticated". `block_iframe_reload` is fired
// when MSAL's hidden-iframe refresh path is blocked (common after inactivity
// or when third-party cookies are restricted); it is NOT an
// InteractionRequiredAuthError, so callers must handle it explicitly.
const INTERACTIVE_RECOVERY_ERROR_CODES = new Set([
  'block_iframe_reload',
  'monitor_window_timeout',
  'timed_out',
  'refresh_token_expired',
  'silent_prompt_value_error',
  'no_account_error',
  'no_token_request_cache_error',
]);

const needsInteractiveRecovery = (error: unknown): boolean => {
  if (error instanceof InteractionRequiredAuthError) return true;
  if (error instanceof AuthError) return INTERACTIVE_RECOVERY_ERROR_CODES.has(error.errorCode);
  return false;
};

// A single, shared one-shot guard for ALL interactive recovery redirects.
// When the MSAL refresh token expires, every silent path (OSDU, Graph, the
// API-client token providers, and session restore) fails at once. Without a
// shared guard they each fire their own redirect, racing and cancelling one
// another (iframe timeouts, double loginRedirect). This ensures exactly ONE
// interactive redirect is attempted per session until it succeeds.
const INTERACTIVE_RECOVERY_GUARD = 'msal_interactive_recovery';
const INTERACTIVE_RECOVERY_GUARD_TS = 'msal_interactive_recovery_ts';
const INTERACTIVE_RECOVERY_GUARD_TTL_MS = 30000;

/**
 * Trigger a single interactive login redirect that re-consents ALL scopes the
 * app needs (base + current-environment OSDU scope). Guarded so it only runs
 * once per session. Returns true if a redirect was initiated (caller should
 * stop further work), false if the guard already fired.
 */
let interactiveRecoveryInFlight = false;
const triggerInteractiveRecovery = async (): Promise<boolean> => {
  const forceTopLevelLogin = () => {
    const loginUrl = `${window.location.origin}/login`;
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = loginUrl;
      } else {
        window.location.assign(loginUrl);
      }
    } catch {
      window.location.assign(loginUrl);
    }
  };

  // Redirect APIs are blocked inside iframes by MSAL. If we ended up in an
  // iframe context after a stale session, force a top-level navigation.
  if (window.self !== window.top) {
    forceTopLevelLogin();
    return true;
  }

  if (interactiveRecoveryInFlight) return true;

  const guard = sessionStorage.getItem(INTERACTIVE_RECOVERY_GUARD);
  const guardTs = Number(sessionStorage.getItem(INTERACTIVE_RECOVERY_GUARD_TS) || '0');
  if (guard) {
    // If a previous recovery attempt is stale (tab resumed after long idle,
    // interrupted redirect flow, etc.), allow one fresh retry.
    if (Date.now() - guardTs > INTERACTIVE_RECOVERY_GUARD_TTL_MS) {
      sessionStorage.removeItem(INTERACTIVE_RECOVERY_GUARD);
      sessionStorage.removeItem(INTERACTIVE_RECOVERY_GUARD_TS);
    } else {
      return false;
    }
  }

  interactiveRecoveryInFlight = true;
  sessionStorage.setItem(INTERACTIVE_RECOVERY_GUARD, '1');
  sessionStorage.setItem(INTERACTIVE_RECOVERY_GUARD_TS, String(Date.now()));
  try {
    const currentEnvConfig = getEnvironmentConfig(getCurrentEnvironment());
    await msalInstance.loginRedirect({
      ...loginRequest,
      scopes: [...loginRequest.scopes, currentEnvConfig.scope],
    });
    return true;
  } catch (redirectError) {
    console.error('Failed to start interactive recovery redirect:', redirectError);
    const errorCode = redirectError instanceof AuthError ? redirectError.errorCode : '';

    // If MSAL blocks redirect due to iframe-reload constraints, fall back to
    // an explicit top-level login navigation instead of hanging on skeleton.
    if (errorCode === 'block_iframe_reload') {
      forceTopLevelLogin();
      return true;
    }

    sessionStorage.removeItem(INTERACTIVE_RECOVERY_GUARD);
    sessionStorage.removeItem(INTERACTIVE_RECOVERY_GUARD_TS);
    interactiveRecoveryInFlight = false;
    return false;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [osduToken, setOsduToken] = useState<string | null>(null);
  const [graphToken, setGraphToken] = useState<string | null>(null);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [dataPartitionId, setDataPartitionIdState] = useState('data');
  const [baseURL, setBaseURLState] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Starting up…');
  const [error, setError] = useState<string | null>(null);
  const [msalInitialized, setMsalInitialized] = useState(false);

  const resetAuthState = () => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setOsduToken(null);
    setGraphToken(null);
  };

  // Set up OSDU token provider for API client.
  // Acquire the token silently on every request so an expired token (e.g. after
  // a period of inactivity) is refreshed transparently via MSAL's cached
  // refresh token — no manual page reload required. Falls back to the last
  // known token in state if there is no signed-in account.
  useEffect(() => {
    setOsduTokenProvider(async () => {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) return osduToken;
      const fresh = await acquireOsduToken(accounts[0]);
      if (fresh && fresh !== osduToken) setOsduToken(fresh);
      return fresh ?? osduToken;
    });
  }, [osduToken]);

  // Set up Graph token provider for API client (silent refresh, same pattern).
  useEffect(() => {
    setGraphTokenProvider(async () => {
      if (msalInstance.getAllAccounts().length === 0) return graphToken;
      const fresh = await acquireGraphToken();
      if (fresh && fresh !== graphToken) setGraphToken(fresh);
      return fresh ?? graphToken;
    });
  }, [graphToken]);

  // Helper function to acquire OSDU token
  const acquireOsduToken = async (account: AccountInfo): Promise<string | null> => {
    try {
      const currentEnvConfig = getEnvironmentConfig(getCurrentEnvironment());
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...getOsduTokenRequest(currentEnvConfig.scope),
        account: account
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (needsInteractiveRecovery(error)) {
        const errCode = error instanceof AuthError ? error.errorCode : 'interaction_required';
        console.log(`Interactive login required for OSDU token (${errCode})`);
        const redirected = await triggerInteractiveRecovery();
        if (!redirected) {
          console.error('OSDU token recovery redirect not triggered; resetting auth state.');
          setError('Session expired. Please sign in again.');
          resetAuthState();
        }
        return null;
      }
      console.error('Failed to acquire OSDU token:', error);
      return null;
    }
  };

  // Helper function to acquire Graph API token (delegated permissions)
  const acquireGraphToken = async (): Promise<string | null> => {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        console.warn('No accounts found for Graph token acquisition');
        return null;
      }

      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...graphTokenRequest,
        account: accounts[0]
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (needsInteractiveRecovery(error)) {
        const errCode = error instanceof AuthError ? error.errorCode : 'interaction_required';
        console.log(`Interactive login required for Graph token (${errCode})`);
        const redirected = await triggerInteractiveRecovery();
        if (!redirected) {
          console.error('Graph token recovery redirect not triggered; resetting auth state.');
          setError('Session expired. Please sign in again.');
          resetAuthState();
        }
        return null;
      }
      console.error('Failed to acquire Graph token:', error);
      return null;
    }
  };

  // Initialize MSAL
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        setLoadingMessage('Initializing authentication…');
        await msalInstance.initialize();
        setMsalInitialized(true);

        // Handle redirect response (including logout redirects)
        setLoadingMessage('Completing sign-in…');
        const response = await msalInstance.handleRedirectPromise();

        // If we have a response, it means we just came back from a redirect
        if (response) {
          // Successful redirect completion — clear the one-shot recovery guard
          // so a future silent failure can trigger another recovery redirect.
          sessionStorage.removeItem(INTERACTIVE_RECOVERY_GUARD);
          sessionStorage.removeItem(INTERACTIVE_RECOVERY_GUARD_TS);
          interactiveRecoveryInFlight = false;
          // This was a login redirect, so we're authenticated
          setUser(response.account);
          setIsAuthenticated(true);
          setToken(response.accessToken);

          // Acquire OSDU token
          setLoadingMessage('Getting OSDU access token…');
          const osduAccessToken = await acquireOsduToken(response.account);
          setOsduToken(osduAccessToken);

          // Acquire Graph token for app registration display names
          setLoadingMessage('Getting directory (Graph) access…');
          const graphAccessToken = await acquireGraphToken();
          setGraphToken(graphAccessToken);
        } else {
          // Check if user is already signed in (but not from a recent redirect)
          setLoadingMessage('Restoring your session…');
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            setUser(accounts[0]);
            setIsAuthenticated(true);

            // Try to get token silently
            try {
              const tokenResponse = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0]
              });
              setToken(tokenResponse.accessToken);

              // Acquire OSDU token
              setLoadingMessage('Getting OSDU access token…');
              const osduAccessToken = await acquireOsduToken(accounts[0]);
              setOsduToken(osduAccessToken);

              // Acquire Graph token for app registration display names
              setLoadingMessage('Getting directory (Graph) access…');
              const graphAccessToken = await acquireGraphToken();
              setGraphToken(graphAccessToken);
            } catch (error) {
              if (needsInteractiveRecovery(error)) {
                // Silent path can't recover (expired / iframe blocked / etc.) —
                // re-run a single interactive login redirect so the user lands
                // back in a working session instead of getting stuck on a
                // skeleton. Shared guard prevents competing redirects.
                console.log('Interactive login required to restore session');
                const redirected = await triggerInteractiveRecovery();
                if (redirected) return;
                // Guard already fired without success — clear local state so the
                // LoginPage shows instead of hanging.
                setIsAuthenticated(false);
                setUser(null);
                setToken(null);
                setOsduToken(null);
                setGraphToken(null);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize MSAL:', error);
        setError('Failed to initialize authentication');
      } finally {
        setIsLoading(false);
      }
    };

    initializeMsal();
  }, []);

  const login = async () => {
    if (!msalInitialized) {
      setError('Authentication system not ready');
      return;
    }

    setLoadingMessage('Redirecting to sign-in…');
    setError(null);
    
    try {
      // Request the OSDU scope as part of sign-in so consent is granted in a
      // single redirect. Otherwise the post-login silent OSDU token acquisition
      // hits consent-required and fires a SECOND redirect, causing the app to
      // reload and re-render home after the skeleton first appears.
      const currentEnvConfig = getEnvironmentConfig(getCurrentEnvironment());
      await msalInstance.loginRedirect({
        ...loginRequest,
        scopes: [...loginRequest.scopes, currentEnvConfig.scope],
      });
    } catch (error) {
      console.error('Login failed:', error);
      setError('Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  const refreshOsduToken = async () => {
    if (!user) {
      console.warn('Cannot refresh OSDU token: no user logged in');
      throw new Error('No user logged in');
    }

    const osduAccessToken = await acquireOsduToken(user);
    if (!osduAccessToken) {
      throw new Error('Failed to acquire OSDU token');
    }
    setOsduToken(osduAccessToken);

    // Also refresh Graph token
    const graphAccessToken = await acquireGraphToken();
    setGraphToken(graphAccessToken);
  };

  const logout = () => {
    if (!msalInitialized) return;

    // Clear local state immediately
    setIsAuthenticated(false);
    setToken(null);
    setOsduToken(null);
    setGraphToken(null);
    setUser(null);

    // Reset environment to dev when logging out
    localStorage.setItem('selectedEnvironment', 'dev');

    // Clear any stored tokens or auth data
    localStorage.removeItem('authToken');
    sessionStorage.clear(); // Clear MSAL cache

    const account = msalInstance.getActiveAccount();
    if (account) {
      // Logout from MSAL and redirect to login page
      msalInstance.logoutRedirect({
        account: account,
        postLogoutRedirectUri: window.location.origin + '/login'
      });
    } else {
      // If no active account, just redirect to login
      window.location.href = '/login';
    }
  };

  const setDataPartitionId = (id: string) => {
    setDataPartitionIdState(id);
    localStorage.setItem('dataPartitionId', id);
  };

  const setBaseURL = (url: string) => {
    setBaseURLState(url);
    localStorage.setItem('baseURL', url);
  };

  const updateToken = (newToken: string) => {
    setToken(newToken);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        token,
        osduToken,
        user,
        dataPartitionId,
        baseURL,
        login,
        logout,
        setDataPartitionId,
        setBaseURL,
        updateToken,
        refreshOsduToken,
        acquireGraphToken,
        isLoading,
        loadingMessage,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}; 